import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type {
  MasteryState,
  ProgressInsight,
  AdaptationEvent,
} from '@coffeeplexity/shared';
import type { ProgressAdjustment } from '../../components/ai/AdaptiveProgressBar';
import type { ProgressUpdate } from '../../components/ai/RealTimeProgressUpdater';
import type { MilestoneCelebration } from '../../components/ai/ProgressMilestoneCelebration';
import type {
  ProgressAnalytics,
  LearningInsight,
} from '../../components/ai/ProgressAnalyticsDashboard';

export interface AdaptiveProgressState {
  // Current progress data
  masteryStates: MasteryState[];
  progressInsights: ProgressInsight[];

  // Real-time updates
  pendingUpdates: ProgressUpdate[];
  confirmedUpdates: ProgressUpdate[];

  // AI adjustments
  activeAdjustments: ProgressAdjustment[];
  adjustmentHistory: ProgressAdjustment[];

  // Milestones and celebrations
  pendingCelebrations: MilestoneCelebration[];
  completedMilestones: string[];

  // Analytics
  analytics: ProgressAnalytics | null;
  learningInsights: LearningInsight[];

  // UI state
  showOptimisticUpdates: boolean;
  celebrationEnabled: boolean;
  notificationsEnabled: boolean;

  // Loading states
  isLoading: boolean;
  isUpdatingProgress: boolean;
  isGeneratingInsights: boolean;

  // Error handling
  error: string | null;
  lastUpdated: string | null;
}

const initialState: AdaptiveProgressState = {
  masteryStates: [],
  progressInsights: [],
  pendingUpdates: [],
  confirmedUpdates: [],
  activeAdjustments: [],
  adjustmentHistory: [],
  pendingCelebrations: [],
  completedMilestones: [],
  analytics: null,
  learningInsights: [],
  showOptimisticUpdates: true,
  celebrationEnabled: true,
  notificationsEnabled: true,
  isLoading: false,
  isUpdatingProgress: false,
  isGeneratingInsights: false,
  error: null,
  lastUpdated: null,
};

// Async thunks
export const fetchProgressAnalytics = createAsyncThunk(
  'adaptiveProgress/fetchAnalytics',
  async (params: {
    userId: string;
    timeRange: '7d' | '30d' | '90d' | '1y';
  }) => {
    // This would call the actual API
    // For now, return mock data
    const mockAnalytics: ProgressAnalytics = {
      userId: params.userId,
      timeRange: params.timeRange,
      overallProgress: {
        currentLevel: 0.75,
        progressRate: 0.12,
        streakDays: 14,
        totalSkills: 25,
        masteredSkills: 18,
      },
      skillProgress: [
        {
          skillId: 'espresso-basics',
          skillName: 'Espresso Basics',
          category: 'Brewing',
          currentMastery: 0.85,
          progressHistory: [
            { date: '2024-01-01', value: 0.6 },
            { date: '2024-01-08', value: 0.7 },
            { date: '2024-01-15', value: 0.85 },
          ],
          trend: 'improving',
          velocity: 0.05,
        },
        {
          skillId: 'latte-art',
          skillName: 'Latte Art',
          category: 'Presentation',
          currentMastery: 0.45,
          progressHistory: [
            { date: '2024-01-01', value: 0.3 },
            { date: '2024-01-08', value: 0.4 },
            { date: '2024-01-15', value: 0.45 },
          ],
          trend: 'improving',
          velocity: 0.03,
        },
      ],
      learningPatterns: {
        optimalLearningTimes: ['9:00 AM', '2:00 PM', '7:00 PM'],
        sessionDuration: 25,
        contentPreferences: [
          { type: 'Video', engagement: 85 },
          { type: 'Recipe', engagement: 92 },
          { type: 'Article', engagement: 68 },
        ],
        difficultyPreference: 0.7,
      },
      insights: [],
      predictions: {
        nextMilestone: {
          skill: 'Espresso Mastery',
          estimatedDate: '2024-02-15',
          confidence: 0.85,
        },
        skillMastery: [
          { skillId: 'espresso-basics', estimatedCompletion: '2024-02-01' },
        ],
        learningEfficiency: 0.78,
      },
    };

    return mockAnalytics;
  }
);

export const updateProgress = createAsyncThunk(
  'adaptiveProgress/updateProgress',
  async (update: Omit<ProgressUpdate, 'id' | 'timestamp'>) => {
    // This would call the actual API to update progress
    const progressUpdate: ProgressUpdate = {
      ...update,
      id: `update_${Date.now()}`,
      timestamp: new Date().toISOString(),
    };

    return progressUpdate;
  }
);

export const generateAIAdjustment = createAsyncThunk(
  'adaptiveProgress/generateAdjustment',
  async (params: { userId: string; skillId: string; context: any }) => {
    // This would call the AI service to generate adjustments
    const adjustment: ProgressAdjustment = {
      id: `adj_${Date.now()}`,
      type: 'difficulty_change',
      reason:
        'Based on your recent performance, we recommend increasing the difficulty to maintain optimal challenge level.',
      impact: 0.15,
      confidence: 0.82,
      aiReasoning:
        "Your success rate on recent exercises (92%) indicates you're ready for more challenging content. This adjustment should accelerate your learning while maintaining engagement.",
      appliedAt: new Date().toISOString(),
    };

    return adjustment;
  }
);

export const generateLearningInsights = createAsyncThunk(
  'adaptiveProgress/generateInsights',
  async (params: { userId: string; masteryStates: MasteryState[] }) => {
    // This would call the AI service to generate insights
    const insights: LearningInsight[] = [
      {
        id: `insight_${Date.now()}`,
        type: 'trend',
        title: 'Consistent Morning Learning',
        description:
          'You show 23% better retention when learning in the morning between 8-10 AM.',
        confidence: 0.87,
        impact: 'medium',
        actionable: true,
        generatedAt: new Date().toISOString(),
      },
      {
        id: `insight_${Date.now() + 1}`,
        type: 'recommendation',
        title: 'Focus on Practical Application',
        description:
          'Your theoretical knowledge is strong, but hands-on practice could accelerate skill development.',
        confidence: 0.74,
        impact: 'high',
        actionable: true,
        generatedAt: new Date().toISOString(),
      },
    ];

    return insights;
  }
);

const adaptiveProgressSlice = createSlice({
  name: 'adaptiveProgress',
  initialState,
  reducers: {
    // Real-time progress updates
    addPendingUpdate: (state, action: PayloadAction<ProgressUpdate>) => {
      state.pendingUpdates.push(action.payload);
    },

    confirmUpdate: (state, action: PayloadAction<string>) => {
      const updateIndex = state.pendingUpdates.findIndex(
        u => u.id === action.payload
      );
      if (updateIndex !== -1) {
        const update = state.pendingUpdates[updateIndex];
        state.confirmedUpdates.push({ ...update, optimistic: false });
        state.pendingUpdates.splice(updateIndex, 1);
      }
    },

    rejectUpdate: (
      state,
      action: PayloadAction<{ updateId: string; reason: string }>
    ) => {
      state.pendingUpdates = state.pendingUpdates.filter(
        u => u.id !== action.payload.updateId
      );
      state.error = `Update rejected: ${action.payload.reason}`;
    },

    // AI adjustments
    addAdjustment: (state, action: PayloadAction<ProgressAdjustment>) => {
      state.activeAdjustments.push(action.payload);
    },

    acceptAdjustment: (state, action: PayloadAction<string>) => {
      const adjustmentIndex = state.activeAdjustments.findIndex(
        a => a.id === action.payload
      );
      if (adjustmentIndex !== -1) {
        const adjustment = state.activeAdjustments[adjustmentIndex];
        state.adjustmentHistory.push(adjustment);
        state.activeAdjustments.splice(adjustmentIndex, 1);
      }
    },

    dismissAdjustment: (state, action: PayloadAction<string>) => {
      state.activeAdjustments = state.activeAdjustments.filter(
        a => a.id !== action.payload
      );
    },

    // Milestones and celebrations
    addCelebration: (state, action: PayloadAction<MilestoneCelebration>) => {
      state.pendingCelebrations.push(action.payload);
    },

    completeCelebration: (state, action: PayloadAction<string>) => {
      state.pendingCelebrations = state.pendingCelebrations.filter(
        c => c.id !== action.payload
      );
      state.completedMilestones.push(action.payload);
    },

    // Mastery states
    updateMasteryStates: (state, action: PayloadAction<MasteryState[]>) => {
      state.masteryStates = action.payload;
      state.lastUpdated = new Date().toISOString();
    },

    updateSingleMasteryState: (state, action: PayloadAction<MasteryState>) => {
      const index = state.masteryStates.findIndex(
        m => m.skillId === action.payload.skillId
      );
      if (index !== -1) {
        state.masteryStates[index] = action.payload;
      } else {
        state.masteryStates.push(action.payload);
      }
      state.lastUpdated = new Date().toISOString();
    },

    // Progress insights
    addProgressInsight: (state, action: PayloadAction<ProgressInsight>) => {
      state.progressInsights.unshift(action.payload);
      // Keep only the latest 20 insights
      if (state.progressInsights.length > 20) {
        state.progressInsights = state.progressInsights.slice(0, 20);
      }
    },

    // Settings
    toggleOptimisticUpdates: state => {
      state.showOptimisticUpdates = !state.showOptimisticUpdates;
    },

    toggleCelebrations: state => {
      state.celebrationEnabled = !state.celebrationEnabled;
    },

    toggleNotifications: state => {
      state.notificationsEnabled = !state.notificationsEnabled;
    },

    // Error handling
    clearError: state => {
      state.error = null;
    },

    // Cleanup
    clearOldUpdates: state => {
      const cutoffTime = new Date(
        Date.now() - 24 * 60 * 60 * 1000
      ).toISOString();
      state.confirmedUpdates = state.confirmedUpdates.filter(
        u => u.timestamp > cutoffTime
      );
    },

    clearOldAdjustments: state => {
      const cutoffTime = new Date(
        Date.now() - 7 * 24 * 60 * 60 * 1000
      ).toISOString();
      state.adjustmentHistory = state.adjustmentHistory.filter(
        a => a.appliedAt > cutoffTime
      );
    },
  },
  extraReducers: builder => {
    // Fetch analytics
    builder
      .addCase(fetchProgressAnalytics.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchProgressAnalytics.fulfilled, (state, action) => {
        state.isLoading = false;
        state.analytics = action.payload;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchProgressAnalytics.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch analytics';
      });

    // Update progress
    builder
      .addCase(updateProgress.pending, state => {
        state.isUpdatingProgress = true;
      })
      .addCase(updateProgress.fulfilled, (state, action) => {
        state.isUpdatingProgress = false;
        state.confirmedUpdates.push(action.payload);
      })
      .addCase(updateProgress.rejected, (state, action) => {
        state.isUpdatingProgress = false;
        state.error = action.error.message || 'Failed to update progress';
      });

    // Generate AI adjustment
    builder.addCase(generateAIAdjustment.fulfilled, (state, action) => {
      state.activeAdjustments.push(action.payload);
    });

    // Generate learning insights
    builder
      .addCase(generateLearningInsights.pending, state => {
        state.isGeneratingInsights = true;
      })
      .addCase(generateLearningInsights.fulfilled, (state, action) => {
        state.isGeneratingInsights = false;
        state.learningInsights = action.payload;
      })
      .addCase(generateLearningInsights.rejected, (state, action) => {
        state.isGeneratingInsights = false;
        state.error = action.error.message || 'Failed to generate insights';
      });
  },
});

export const {
  addPendingUpdate,
  confirmUpdate,
  rejectUpdate,
  addAdjustment,
  acceptAdjustment,
  dismissAdjustment,
  addCelebration,
  completeCelebration,
  updateMasteryStates,
  updateSingleMasteryState,
  addProgressInsight,
  toggleOptimisticUpdates,
  toggleCelebrations,
  toggleNotifications,
  clearError,
  clearOldUpdates,
  clearOldAdjustments,
} = adaptiveProgressSlice.actions;

export default adaptiveProgressSlice.reducer;

// Selectors
export const selectMasteryStates = (state: {
  adaptiveProgress: AdaptiveProgressState;
}) => state.adaptiveProgress.masteryStates;

export const selectProgressInsights = (state: {
  adaptiveProgress: AdaptiveProgressState;
}) => state.adaptiveProgress.progressInsights;

export const selectPendingUpdates = (state: {
  adaptiveProgress: AdaptiveProgressState;
}) => state.adaptiveProgress.pendingUpdates;

export const selectActiveAdjustments = (state: {
  adaptiveProgress: AdaptiveProgressState;
}) => state.adaptiveProgress.activeAdjustments;

export const selectPendingCelebrations = (state: {
  adaptiveProgress: AdaptiveProgressState;
}) => state.adaptiveProgress.pendingCelebrations;

export const selectProgressAnalytics = (state: {
  adaptiveProgress: AdaptiveProgressState;
}) => state.adaptiveProgress.analytics;

export const selectLearningInsights = (state: {
  adaptiveProgress: AdaptiveProgressState;
}) => state.adaptiveProgress.learningInsights;

export const selectProgressSettings = (state: {
  adaptiveProgress: AdaptiveProgressState;
}) => ({
  showOptimisticUpdates: state.adaptiveProgress.showOptimisticUpdates,
  celebrationEnabled: state.adaptiveProgress.celebrationEnabled,
  notificationsEnabled: state.adaptiveProgress.notificationsEnabled,
});

export const selectProgressLoadingState = (state: {
  adaptiveProgress: AdaptiveProgressState;
}) => ({
  isLoading: state.adaptiveProgress.isLoading,
  isUpdatingProgress: state.adaptiveProgress.isUpdatingProgress,
  isGeneratingInsights: state.adaptiveProgress.isGeneratingInsights,
  error: state.adaptiveProgress.error,
});
